
import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Tag, Button, List, Typography, Space } from 'antd';
import { AlertOutlined, HistoryOutlined, CheckCircleOutlined, DollarOutlined, ShopOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Contractor, ContractorStatus } from '../../crm/types/contractor';
import { ordersApi } from '../../../api/client';

const { Text } = Typography;

interface ContractorMiniInfoProps {
    contractor: Contractor | null;
}

export const ContractorMiniInfo: React.FC<ContractorMiniInfoProps> = ({ contractor }) => {
    const [lastOrders, setLastOrders] = useState<any[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        if (contractor) {
            fetchHistory();
        } else {
            setLastOrders([]);
        }
    }, [contractor?.id]);

    const fetchHistory = async () => {
        if (!contractor) return;
        setLoadingHistory(true);
        try {
            // Retrieve orders by customerName or email (if present)
            // Using a specific search or a dedicated endpoint would be better, but search works for now
            // Or better: filter by contractorId if we start saving it (which we do now!)
            // We need to implement filtering by contractorId in OrdersService.getStats or getAll
            // For now, let's search by name as a fallback or use available filters

            // Actually, we haven't updated client.ts to support filtering by contractorId yet, 
            // but we can search by name which is robust enough for now if names are unique-ish.
            // Or we can assume the new backend update allows filtering by customerId/externalId/search.
            // Let's rely on search by name for this MVP as it's non-breaking.

            const response = await ordersApi.getAll({
                search: contractor.nip || contractor.name, // NIP is more unique
                limit: 3,
                sortField: 'orderDate',
                sortOrder: 'DESC'
            });
            setLastOrders(response.data.data);
        } catch (error) {
            console.error('Failed to fetch history', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    if (!contractor) return null;

    const isBlocked = contractor.status === ContractorStatus.BLACKLIST;

    return (
        <Card
            size="small"
            title={
                <Space>
                    <ShopOutlined />
                    {contractor.name}
                    {isBlocked && <Tag color="error" icon={<AlertOutlined />}>ZABLOKOWANY</Tag>}
                </Space>
            }
            style={{
                marginTop: 16,
                borderColor: isBlocked ? '#ffa39e' : '#d9d9d9',
                background: isBlocked ? '#fff1f0' : undefined
            }}
        >
            <Descriptions column={2} size="small">
                <Descriptions.Item label="NIP">{contractor.nip || '-'}</Descriptions.Item>
                <Descriptions.Item label="Płatność">{contractor.paymentTerms || 'Domyślna'}</Descriptions.Item>

                <Descriptions.Item label="Adres">
                    {contractor.invoiceAddress?.city || '-'}
                </Descriptions.Item>
            </Descriptions>

            {contractor.fulfillmentNotes && (
                <div style={{ marginTop: 8, padding: 8, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4 }}>
                    <AlertOutlined style={{ color: '#faad14', marginRight: 8 }} />
                    <Text type="warning" strong>Uwagi:</Text> {contractor.fulfillmentNotes}
                </div>
            )}

            <div style={{ marginTop: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}><HistoryOutlined /> Ostatnie zamówienia</Text>
                <List
                    loading={loadingHistory}
                    size="small"
                    dataSource={lastOrders}
                    renderItem={(item: any) => (
                        <List.Item style={{ padding: '4px 0', border: 'none' }}>
                            <Space split="|">
                                <Text code>{item.orderNumber}</Text>
                                <Text>{dayjs(item.orderDate || item.createdAt).format('DD.MM.YYYY')}</Text>
                                <Tag color={item.paymentStatus === 'PAID' ? 'green' : 'gold'}>
                                    {item.total} PLN
                                </Tag>
                            </Space>
                        </List.Item>
                    )}
                />
                {lastOrders.length === 0 && !loadingHistory && (
                    <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>Brak historii zamówień</div>
                )}
            </div>
        </Card>
    );
};

